import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FolderOpen } from 'lucide-react';
import { api } from '@/lib/api';
import type { QueueResponse } from '@/hooks/useApproval';
import { PageHeader, PageShell } from '@/components/PageShell';
import { ClaimTypeBadge } from '@/pages/ClaimsPage';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatMonth(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    month: 'short', year: 'numeric',
  });
}

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'bg-surface-2 text-text-3',
  SUBMITTED: 'bg-info-soft text-info',
  RETURNED_TO_ADMIN: 'bg-warn-soft text-warn',
  ADMIN_APPROVED: 'bg-info-soft text-info',
  FINANCE_APPROVED: 'bg-info-soft text-info',
  APPROVED: 'bg-ok-soft text-ok',
  REJECTED: 'bg-danger-soft text-danger',
};

export function AdminAllClaimsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [monthFilter, setMonthFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const { data, isLoading, error } = useQuery<QueueResponse>({
    queryKey: ['admin', 'all-claims', { page, statusFilter, typeFilter, monthFilter, searchFilter }],
    queryFn: () => {
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (typeFilter !== 'ALL') params.append('type', typeFilter);
      if (monthFilter) params.append('month', monthFilter);
      if (searchFilter) params.append('search', searchFilter);
      return api.get(`/admin/claims?${params.toString()}`);
    },
  });

  return (
    <PageShell>
      <PageHeader
        title="All Claims History"
        description="View all claims across the system with advanced filtering."
      />

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-border bg-surface p-4">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-semibold text-text-2 mb-1">Search User or Ref NO</label>
          <input
            type="text"
            placeholder="Search by name, email or reference..."
            value={searchFilter}
            onChange={(e) => { setSearchFilter(e.target.value); setPage(1); }}
            className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-surface focus:outline-2 focus:ring-primary/50 text-text"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-text-2 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="text-sm px-3 py-2 rounded-lg border border-border bg-surface text-text focus:outline-2 focus:ring-primary/50"
          >
            <option value="ALL">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="SUBMITTED">Submitted (Pending Admin)</option>
            <option value="RETURNED_TO_ADMIN">Returned to Admin</option>
            <option value="ADMIN_APPROVED">Admin Approved (Pending Finance)</option>
            <option value="FINANCE_APPROVED">Finance Approved (Pending HR)</option>
            <option value="APPROVED">Fully Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-text-2 mb-1">Type</label>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="text-sm px-3 py-2 rounded-lg border border-border bg-surface text-text focus:outline-2 focus:ring-primary/50"
          >
            <option value="ALL">All Types</option>
            <option value="GENERAL">General</option>
            <option value="MILEAGE">Mileage</option>
            <option value="PETTY_CASH">Petty Cash</option>
            <option value="COMBINED">Combined</option>
            <option value="OTHER">Other</option>
            <option value="PROJECT">Project</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-text-2 mb-1">Claim Month</label>
          <input
            type="month"
            value={monthFilter}
            onChange={(e) => { setMonthFilter(e.target.value); setPage(1); }}
            className="text-sm px-3 py-2 rounded-lg border border-border bg-surface text-text focus:outline-2 focus:ring-primary/50"
          />
        </div>
      </div>

      {/* Loading Skeleton */}
      {isLoading && (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-surface border border-border animate-pulse" />
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-xl bg-danger-soft border border-danger/20 px-4 py-3 text-sm text-danger">
          Failed to load claims history.
        </div>
      )}

      {/* Table / List */}
      {!isLoading && data && data.claims.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-surface/50 p-14 text-center space-y-3">
          <FolderOpen className="h-10 w-10 mx-auto text-text-2/40" />
          <h2 className="text-lg font-semibold text-text">No claims found</h2>
          <p className="text-sm text-text-2">Adjust your filters to see more results.</p>
        </div>
      ) : (
        data && data.claims.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-border bg-surface-2/50 text-xs text-text-2">
                  <th className="font-semibold p-4">Ref #</th>
                  <th className="font-semibold p-4">Type</th>
                  <th className="font-semibold p-4">User</th>
                  <th className="font-semibold p-4">Month</th>
                  <th className="font-semibold p-4">Amount</th>
                  <th className="font-semibold p-4">Status</th>
                  <th className="font-semibold p-4">Submitted Date</th>
                  <th className="font-semibold p-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                {data.claims.map(claim => (
                  <tr key={claim.id} className="hover:bg-surface-2 transition-colors">
                    <td className="p-4 font-mono text-xs text-primary">{claim.referenceNumber ?? 'N/A'}</td>
                    <td className="p-4">
                      <ClaimTypeBadge type={claim.claimType} />
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-text">{claim.user.name}</p>
                      <p className="text-xs text-text-2">{claim.user.email}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-text">{formatMonth(claim.claimMonth)}</p>
                      {claim.description && (
                        <p className="text-xs text-text-2 truncate max-w-[180px]" title={claim.description}>
                          {claim.description}
                        </p>
                      )}
                    </td>
                    <td className="p-4 font-semibold text-text">{formatCurrency(claim.totalAmount)}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${STATUS_BADGE[claim.status] || 'bg-surface-2 text-text-2'}`}>
                        {claim.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-4 text-text-2">{formatDate(claim.updatedAt)}</td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => navigate(`/claims/${claim.id}`)}
                        className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Pagination */}
      {data && data.meta.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm pt-4 border-t border-border">
          <p className="text-text-2">
            Showing page {page} of {data.meta.pagination.totalPages} ({data.meta.pagination.total} claims total)
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-border font-medium bg-surface hover:bg-surface-2 disabled:opacity-50 transition-colors"
            >
              Prev
            </button>
            <button
              onClick={() => setPage(p => Math.min(data.meta.pagination.totalPages, p + 1))}
              disabled={page === data.meta.pagination.totalPages}
              className="px-3 py-1.5 rounded-lg border border-border font-medium bg-surface hover:bg-surface-2 disabled:opacity-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </PageShell>
  );
}
